import { elementGroupRequirements } from '../data/elementGroupRequirements';
import { connectionValueRules } from '../data/connectionValueRules';

function getDifficultyLevel(value) {
  if (value <= 0.2) return 'A/B/C';
  return 'D or higher';
}

function qualifiesForFullEGCredit(event, group, skillValue, level) {
  const eg1ExceptionEvents = new Set(['FX', 'PH', 'SR', 'PB', 'HB']);
  const isEg1Exception = group === 'EG I' && eg1ExceptionEvents.has(event);

  if (isEg1Exception) {
    return skillValue >= 0.1;
  }

  if (level === 7 || level === 8) {
    return skillValue >= 0.1;
  }

  if (level === 9) {
    return skillValue >= 0.3;
  }

  if (level === 10) {
    return skillValue >= 0.4;
  }

  return skillValue >= 0.1;
}

function calculateEGBonus(event, group, skillValue, level) {
  const requirements = elementGroupRequirements.find(req => req.apparatus === event);
  if (!requirements || typeof requirements['EG Bonus'] === 'string') return 0;

  const egBonus = requirements['EG Bonus'];
  if (typeof egBonus === 'string') return 0;

  if (level === 8) {
    return 0.5;
  }

  if (level === 7) {
    return 0.5;
  }

  if (level === 9 || level === 10) {
    return qualifiesForFullEGCredit(event, group, skillValue, level) ? 0.5 : 0;
  }

  if (group === 'EG I') return egBonus['EG I'];

  const diffLevel = getDifficultyLevel(skillValue);
  const groupBonus = egBonus[group];
  return groupBonus?.[diffLevel] || 0;
}

function calculateConnectionValue(event, skills) {
  if (skills.length < 2) return 0;

  const rules = connectionValueRules.find(rule => rule.apparatus === event);
  if (!rules || rules.valid_connections.length === 0) return 0;

  let totalCV = 0;
  for (let i = 0; i < skills.length - 1; i++) {
    const current = skills[i];
    const next = skills[i + 1];

    for (const connection of rules.valid_connections) {
      if (connection.type === 'C + D or higher' &&
        current.value >= 0.3 && next.value >= 0.4) {
        totalCV += connection.cv_bonus;
      } else if (connection.type === 'D + D' &&
        current.value >= 0.4 && next.value >= 0.4) {
        totalCV += connection.cv_bonus;
      } else if (connection.type === 'D + E or higher' &&
        current.value >= 0.4 && next.value >= 0.5) {
        totalCV += connection.cv_bonus;
      }
    }
  }

  return totalCV;
}

function calculateGymActEGBonus(event, group, skillValue) {
  if (group === 'EG I') {
    return skillValue >= 0.1 ? 0.5 : 0;
  }

  const isFxEg4 = event === 'FX' && group === 'EG IV';
  if (group === 'EG II' || group === 'EG III' || isFxEg4) {
    if (skillValue <= 0.2) return 0.3;
    if (skillValue === 0.3) return 0.4;
    return 0.5;
  }

  if (group === 'EG IV') {
    return skillValue;
  }

  return 0;
}

function calculateGymActStickBonus(event, skills, applied) {
  if (!applied) return 0;
  if (!['FX', 'SR', 'PB', 'HB'].includes(event)) return 0;

  const dismountSkill = skills
    .filter(skill => skill.group === 'EG IV')
    .sort((a, b) => b.value - a.value)[0];

  if (!dismountSkill) return 0;

  if (dismountSkill.value >= 0.3) return 0.2;
  if (dismountSkill.value >= 0.2) return 0.1;
  return 0;
}

export function calculateStartValueGymAct(skills, event, bonuses = [], level = 8, gymActStickApplied = false) {
  const activeSkills = skills.filter(skill => skill.active !== false);
  let countableSkills = [...activeSkills];

  if (event === 'FX') {
    const strengthSkills = countableSkills.filter(skill => skill.name.toLowerCase().includes('strength') || skill.name.toLowerCase().includes('hold'));
    const nonStrengthSkills = countableSkills.filter(skill => !skill.name.toLowerCase().includes('strength') && !skill.name.toLowerCase().includes('hold'));
    countableSkills = [...nonStrengthSkills, ...(strengthSkills.slice(0, 1))];

    const circleSkills = countableSkills.filter(skill => skill.name.toLowerCase().includes('circle') || skill.name.toLowerCase().includes('flair'));
    const nonCircleSkills = countableSkills.filter(skill => !skill.name.toLowerCase().includes('circle') && !skill.name.toLowerCase().includes('flair'));
    countableSkills = [...nonCircleSkills, ...(circleSkills.slice(0, 1))];
  }

  if (event === 'SR') {
    const strengthSkills = countableSkills.filter(skill =>
      skill.name.toLowerCase().includes('strength') ||
      skill.name.toLowerCase().includes('hold')
    );
    const nonStrengthSkills = countableSkills.filter(skill =>
      !skill.name.toLowerCase().includes('strength') &&
      !skill.name.toLowerCase().includes('hold')
    );
    const handstandTurnSkills = strengthSkills.filter(skill =>
      skill.name.toLowerCase().includes('handstand') ||
      skill.name.toLowerCase().includes('turn')
    );
    const otherStrengthSkills = strengthSkills.filter(skill =>
      !skill.name.toLowerCase().includes('handstand') &&
      !skill.name.toLowerCase().includes('turn')
    );
    countableSkills = [
      ...nonStrengthSkills,
      ...handstandTurnSkills,
      ...(otherStrengthSkills.slice(0, 1))
    ];
  }

  let sorted = [];
  let baseDScore = 0;

  if (event === 'VT') {
    if (countableSkills.length > 0) {
      baseDScore = countableSkills[0].value;
      sorted = [countableSkills[0]];
    } else {
      baseDScore = 0;
      sorted = [];
    }
  } else {
    sorted = [...countableSkills]
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    baseDScore = sorted.reduce((sum, skill) => sum + skill.value, 0);
  }

  let composition = 0;
  const egCoverage = {
    'EG I': false,
    'EG II': false,
    'EG III': false,
    'EG IV': false
  };
  const validEGs = new Set(['EG I', 'EG II', 'EG III', 'EG IV']);

  sorted.forEach(skill => {
    const isJuniorA =
      skill.name.toLowerCase().includes('junior a') ||
      (skill.code && skill.code.toLowerCase().includes('jr-'));

    if (isJuniorA) {
      return;
    }

    const effectiveGroup = skill.group;

    if (validEGs.has(effectiveGroup) && !egCoverage[effectiveGroup]) {
      egCoverage[effectiveGroup] = true;
      composition += calculateGymActEGBonus(event, effectiveGroup, skill.value);
    }
  });

  const missingEGs = Object.values(egCoverage).filter(covered => !covered).length;
  const connectionBonus = calculateConnectionValue(event, sorted);
  const additionalBonus = bonuses
    .filter(bonus => bonus.applied)
    .reduce((sum, bonus) => sum + bonus.value, 0);

  const stickBonus = calculateGymActStickBonus(event, sorted, gymActStickApplied);

  let missingSkillDeduction = 0;
  if (event !== 'VT') {
    const recognizedSkills = sorted.length;
    if (recognizedSkills < 6) {
      missingSkillDeduction = (recognizedSkills - 6) * 1.0;
    }
  }

  let startValue;
  if (event === 'VT') {
    startValue = baseDScore + additionalBonus + stickBonus;
  } else {
    startValue = baseDScore + composition + connectionBonus + additionalBonus + stickBonus + missingSkillDeduction;
  }

  return {
    total: parseFloat(startValue.toFixed(1)),
    breakdown: {
      baseDScore: parseFloat(baseDScore.toFixed(1)),
      composition: parseFloat(composition.toFixed(1)),
      connectionValue: parseFloat(connectionBonus.toFixed(1)),
      bonus: parseFloat(additionalBonus.toFixed(1)),
      stickBonus: parseFloat(stickBonus.toFixed(1)),
      egDeduction: 0,
      missingSkillDeduction: parseFloat(missingSkillDeduction.toFixed(1))
    },
    egCoverage,
    missingEGs
  };
}

export function calculateStartValue(skills, event, bonuses = [], level = 8) {
  const activeSkills = skills.filter(skill => skill.active !== false);

  let countableSkills = [...activeSkills];

  if (event === 'FX') {
    const strengthSkills = countableSkills.filter(skill => skill.name.toLowerCase().includes('strength') || skill.name.toLowerCase().includes('hold'));
    const nonStrengthSkills = countableSkills.filter(skill => !skill.name.toLowerCase().includes('strength') && !skill.name.toLowerCase().includes('hold'));
    countableSkills = [...nonStrengthSkills, ...(strengthSkills.slice(0, 1))];

    const circleSkills = countableSkills.filter(skill => skill.name.toLowerCase().includes('circle') || skill.name.toLowerCase().includes('flair'));
    const nonCircleSkills = countableSkills.filter(skill => !skill.name.toLowerCase().includes('circle') && !skill.name.toLowerCase().includes('flair'));
    countableSkills = [...nonCircleSkills, ...(circleSkills.slice(0, 1))];
  }

  if (event === 'SR') {
    const strengthSkills = countableSkills.filter(skill =>
      skill.name.toLowerCase().includes('strength') ||
      skill.name.toLowerCase().includes('hold')
    );
    const nonStrengthSkills = countableSkills.filter(skill =>
      !skill.name.toLowerCase().includes('strength') &&
      !skill.name.toLowerCase().includes('hold')
    );
    const handstandTurnSkills = strengthSkills.filter(skill =>
      skill.name.toLowerCase().includes('handstand') ||
      skill.name.toLowerCase().includes('turn')
    );
    const otherStrengthSkills = strengthSkills.filter(skill =>
      !skill.name.toLowerCase().includes('handstand') &&
      !skill.name.toLowerCase().includes('turn')
    );
    countableSkills = [
      ...nonStrengthSkills,
      ...handstandTurnSkills,
      ...(otherStrengthSkills.slice(0, 1))
    ];
  }

  let sorted = [];
  let baseDScore = 0;

  if (event === 'VT') {
    if (countableSkills.length > 0) {
      baseDScore = countableSkills[0].value;
      sorted = [countableSkills[0]];
    } else {
      baseDScore = 0;
      sorted = [];
    }
  } else {
    sorted = [...countableSkills]
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
    baseDScore = sorted.reduce((sum, skill) => sum + skill.value, 0);
  }

  let composition = 0;
  const egCoverage = {
    'EG I': false,
    'EG II': false,
    'EG III': false,
    'EG IV': false
  };
  const validEGs = new Set(['EG I', 'EG II', 'EG III', 'EG IV']);
  const coveredEGsList = [];

  if (event === 'VT') {
    composition = 0;
  }

  sorted.forEach(skill => {
    const isJuniorA =
      skill.name.toLowerCase().includes('junior a') ||
      (skill.code && skill.code.toLowerCase().includes('jr-'));

    if (isJuniorA) {
      return;
    }

    let effectiveGroup = skill.group;
    if (event === 'SR' && level === 8 && skill.group === 'EG II' &&
      (skill.name.toLowerCase().includes('handstand') ||
        skill.name.toLowerCase().includes('hdst')) &&
      skill.name.toLowerCase().includes('2 s')) {
      effectiveGroup = 'EG III';
    }

    if (event === 'HB' && level === 8 && skill.name.toLowerCase().includes('endo')) {
      effectiveGroup = 'EG II';
    }

    const qualifiesForEGCredit = qualifiesForFullEGCredit(event, effectiveGroup, skill.value, level);

    if (validEGs.has(effectiveGroup) && !egCoverage[effectiveGroup] && qualifiesForEGCredit) {
      egCoverage[effectiveGroup] = true;
      coveredEGsList.push(effectiveGroup);
      if (level === 7) {
        if (coveredEGsList.length <= 3) {
          composition += 0.5;
        }
      } else {
        composition += calculateEGBonus(event, effectiveGroup, skill.value, level);
      }
    }
  });

  const missingEGs = Object.values(egCoverage).filter(covered => !covered).length;

  let egDeduction = 0;
  let effectiveMissingEGs = missingEGs;

  if (level === 7) {
    egDeduction = 0;
    effectiveMissingEGs = Math.max(0, 3 - coveredEGsList.length);
  } else {
    egDeduction = 0;
  }

  const connectionBonus = calculateConnectionValue(event, sorted);

  const additionalBonus = bonuses
    .filter(bonus => bonus.applied)
    .reduce((sum, bonus) => sum + bonus.value, 0);

  let missingSkillDeduction = 0;
  if ([7, 8, 9].includes(level) && event !== 'VT') {
    const recognizedSkills = sorted.length;
    if (recognizedSkills < 6) {
      missingSkillDeduction = (recognizedSkills - 6) * 1.0;
    }
  }

  let startValue;
  if (event === 'VT') {
    startValue = baseDScore + additionalBonus;
  } else {
    startValue = baseDScore + composition + connectionBonus + additionalBonus + egDeduction + missingSkillDeduction;
  }

  return {
    total: parseFloat(startValue.toFixed(1)),
    breakdown: {
      baseDScore: parseFloat(baseDScore.toFixed(1)),
      composition: parseFloat(composition.toFixed(1)),
      connectionValue: parseFloat(connectionBonus.toFixed(1)),
      bonus: parseFloat(additionalBonus.toFixed(1)),
      egDeduction: parseFloat(egDeduction.toFixed(1)),
      missingSkillDeduction: parseFloat(missingSkillDeduction.toFixed(1))
    },
    egCoverage,
    missingEGs: level === 7 ? effectiveMissingEGs : missingEGs
  };
}
